// Import necessary hooks and components
import { useState } from 'react';
import { useJobPosting } from '../../hooks/useJobPosting'; // Custom hook to handle job posting API calls
import { useResponsibleData } from '../../hooks/useResponsibleData'; // Custom hook to fetch responsible user data
import { LoadingSpinner } from '../LoadingSpinner'; // Spinner component for loading states
import toast from 'react-hot-toast'; // Notification library for user feedback

// Define the props for the CreateJobForm component
interface CreateJobFormProps {
  onSuccess: () => void; // Callback triggered on successful job creation
  onCancel: () => void; // Callback to cancel the form
}

/**
 * Component for creating a new job with a multi-step form.
 * Handles fetching responsible data, user input, and form submission.
 */
export function CreateJobForm({ onSuccess, onCancel }: CreateJobFormProps) {
  // Destructure methods and states from hooks
  const { createJob, loading: createLoading } = useJobPosting(); // Hook for job creation API
  const { responsibleData, loading: dataLoading, error } = useResponsibleData(); // Hook for responsible data

  // Form state for job details
  const [formData, setFormData] = useState({
    job_title: '', // Job title
    description: '', // Job description
    business_sector: '', // Business sector
    required_experience: '', // Required experience
    contract: 'CDI', // Default contract type
    salary: 0, // Salary
    city: '', // City
    postal_code: '', // Postal code
    active: true, // Job status
  });

  // State to track the current form step
  const [step, setStep] = useState(1);

  // Display a loading spinner while fetching responsible data
  if (dataLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <LoadingSpinner />
      </div>
    );
  }

  // Display an error message if responsible data fails to load
  if (error || !responsibleData?.business) {
    return (
      <div className="text-red-500 p-4 bg-red-50 rounded-lg">
        {error || 'Unable to load business data'}
      </div>
    );
  }

  const { business } = responsibleData; // Extract business information from responsible data

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); // Prevent default form submission behavior

    try {
      // Call the API to create a job with additional business-related data
      await createJob({
        ...formData,
        business: business.company_name,
        business_email: business.email,
        address_t: business.company_address,
        state: business.area,
        responsibles_id: responsibleData.id,
      });

      toast.success('Offre d\'emploi créée avec succès'); // Display success notification
      onSuccess(); // Trigger success callback
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Erreur lors de la création de l\'offre';
      toast.error(message); // Display error notification
    }
  };

  // Handle input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'salary' || name === 'postal_code' ? Number(value) : value, // Parse numeric values
    }));
  };

  // Navigate to the next form step
  const goNext = () => setStep((prev) => prev + 1);

  // Navigate to the previous form step
  const goBack = () => setStep((prev) => prev - 1);

  // Render the multi-step form
  return (
    <div className="p-6 bg-primary-lightest rounded-lg shadow-md w-full sm:max-w-lg mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Step 1: Job Title & Sector */}
        {step === 1 && (
          <>
            <h3 className="text-xl font-semibold text-primary-dark">Étape 1: Informations de base</h3>
            <div>
              <label htmlFor="job_title" className="block text-sm font-medium text-primary-dark">
                Titre du poste
              </label>
              <input
                type="text"
                id="job_title"
                name="job_title"
                value={formData.job_title}
                onChange={handleChange}
                required
                className="input-primary"
              />
            </div>
            <div>
              <label htmlFor="business_sector" className="block text-sm font-medium text-primary-dark">
                Secteur d'activité
              </label>
              <input
                type="text"
                id="business_sector"
                name="business_sector"
                value={formData.business_sector}
                onChange={handleChange}
                required
                className="input-primary"
              />
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={goNext} className="btn-primary">
                Suivant
              </button>
            </div>
          </>
        )}

        {/* Step 2: Location & Salary */}
        {step === 2 && (
          <>
            <h3 className="text-xl font-semibold text-primary-dark">Étape 2: Localisation & Salaire</h3>
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-primary-dark">
                Ville
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                className="input-primary"
              />
            </div>
            <div>
              <label htmlFor="postal_code" className="block text-sm font-medium text-primary-dark">
                Code postal
              </label>
              <input
                type="number"
                id="postal_code"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                required
                className="input-primary"
              />
            </div>
            <div>
              <label htmlFor="salary" className="block text-sm font-medium text-primary-dark">
                Salaire annuel (€)
              </label>
              <input
                type="number"
                id="salary"
                name="salary"
                value={formData.salary}
                onChange={handleChange}
                min="0"
                step="1000"
                required
                className="input-primary"
              />
            </div>
            <div className="flex justify-between">
              <button type="button" onClick={goBack} className="btn-secondary">
                Précédent
              </button>
              <button type="button" onClick={goNext} className="btn-primary">
                Suivant
              </button>
            </div>
          </>
        )}

        {/* Step 3: Description & Contract */}
        {step === 3 && (
          <>
            <h3 className="text-xl font-semibold text-primary-dark">Étape 3: Description & Contrat</h3>
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-primary-dark">
                Description du poste
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                required
                className="input-primary"
              />
            </div>
            <div>
              <label htmlFor="contract" className="block text-sm font-medium text-primary-dark">
                Type de contrat
              </label>
              <select
                id="contract"
                name="contract"
                value={formData.contract}
                onChange={handleChange}
                className="input-primary"
              >
                <option value="CDI">CDI</option>
                <option value="CDD">CDD</option>
                <option value="Interim">Intérim</option>
                <option value="Stage">Stage</option>
                <option value="Alternance">Alternance</option>
              </select>
            </div>
            <div className="flex justify-between">
              <button type="button" onClick={goBack} className="btn-secondary">
                Précédent
              </button>
              <button type="submit" disabled={createLoading} className="btn-primary">
                {createLoading ? 'Création...' : 'Créer l\'offre'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
